import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Subject } from 'rxjs';
import { io, Socket } from 'socket.io-client';

import { environment } from '../../../environments/environment';
import { RequestService } from '../request.service';
import {
  ActualizarNotificacionResponseDTO,
  CambioNotificacionesSocketDTO,
  ContadorNotificacionesDTO,
  ContadorNotificacionesResponseDTO,
  ListarNotificacionesFiltroDTO,
  PaginaNotificacionesDTO,
  PaginaNotificacionesResponseDTO,
} from './DTO/notificacion.dto';
import { CambioPublicacionesSocketDTO } from './DTO/publicacion-plataforma.dto';

@Injectable({ providedIn: 'root' })
export class NotificacionesWorkspaceService {
  private socket: Socket | null = null;
  private cambiosSubject = new Subject<CambioNotificacionesSocketDTO>();
  private reconexionSubject = new Subject<void>();
  private publicacionesSubject = new Subject<CambioPublicacionesSocketDTO>();

  readonly cambios$ = this.cambiosSubject.asObservable();
  readonly reconexion$ = this.reconexionSubject.asObservable();
  readonly publicaciones$ = this.publicacionesSubject.asObservable();

  constructor(
    private http: HttpClient,
    private requestServ: RequestService
  ) {}

  /**
   * Construye headers silenciosos con el JWT de la sesión activa; el backend
   * determina usuario y empresa y el contador no activa el overlay global.
   */
  private construirHeaders(): HttpHeaders {
    return new HttpHeaders({
      token: this.requestServ.master.apiKey,
      'X-Skip-Loading': 'true',
    });
  }

  /**
   * Extrae el mensaje normalizado del contrato crearRespuesta o del error HTTP
   * para que el panel decida si debe mostrar un estado recuperable.
   */
  private obtenerMensajeError(error: any, respaldo: string): string {
    return error?.error?.respuesta || error?.message || respaldo;
  }

  /**
   * Convierte apiUrlV2 en el origen donde comparte servidor HTTP Socket.IO;
   * no incorpora rutas de API porque el handshake usa /socket.io.
   */
  private obtenerOrigenSocket(): string {
    return environment.apiUrlV2.replace(/\/api\/v2\/?$/, '');
  }

  /**
   * Consulta la bandeja tenant con filtros y paginación SQL; nunca envía un
   * identificador de usuario porque el backend lo toma del JWT autenticado.
   */
  async listar(
    filtros: ListarNotificacionesFiltroDTO
  ): Promise<PaginaNotificacionesDTO> {
    let params = new HttpParams();
    if (Number(filtros.page) > 0) {
      params = params.set('page', String(filtros.page));
    }
    if (Number(filtros.limit) > 0) {
      params = params.set('limit', String(filtros.limit));
    }
    if (filtros.estado) {
      params = params.set('estado', filtros.estado);
    }
    if (filtros.codigo) {
      params = params.set('codigo', filtros.codigo);
    }

    try {
      const response = await this.http
        .get<PaginaNotificacionesResponseDTO>(
          `${environment.apiUrlV2}/workspace/notificaciones`,
          { headers: this.construirHeaders(), params }
        )
        .toPromise();
      if (response.error) {
        throw new Error(String(response.respuesta || 'Error al consultar notificaciones'));
      }
      return response.respuesta as PaginaNotificacionesDTO;
    } catch (error) {
      throw new Error(
        this.obtenerMensajeError(error, 'No se pudieron consultar las notificaciones')
      );
    }
  }

  /**
   * Obtiene el número de entregas activas no leídas del usuario y empresa del
   * JWT; se usa al iniciar, reconectar y recibir un aviso Socket.IO.
   */
  async contarNoLeidas(): Promise<ContadorNotificacionesDTO> {
    try {
      const response = await this.http
        .get<ContadorNotificacionesResponseDTO>(
          `${environment.apiUrlV2}/workspace/notificaciones/contador`,
          { headers: this.construirHeaders() }
        )
        .toPromise();
      if (response.error) {
        throw new Error(String(response.respuesta || 'Error al contar notificaciones'));
      }
      return response.respuesta as ContadorNotificacionesDTO;
    } catch (error) {
      throw new Error(
        this.obtenerMensajeError(error, 'No se pudo actualizar el contador')
      );
    }
  }

  /**
   * Marca una entrega propia como leída; la propiedad se valida nuevamente en
   * el backend y el cambio individual no altera a otros destinatarios.
   */
  async marcarLeida(idNotificacion: number): Promise<void> {
    try {
      const response = await this.http
        .patch<ActualizarNotificacionResponseDTO>(
          `${environment.apiUrlV2}/workspace/notificaciones/${idNotificacion}/leer`,
          {},
          { headers: this.construirHeaders() }
        )
        .toPromise();
      if (response.error) throw new Error(String(response.respuesta));
    } catch (error) {
      throw new Error(
        this.obtenerMensajeError(error, 'No se pudo marcar la notificación')
      );
    }
  }

  /**
   * Marca en una operación backend todas las entregas visibles como leídas y
   * devuelve cuántas filas fueron actualizadas realmente.
   */
  async marcarTodasLeidas(): Promise<number> {
    try {
      const response = await this.http
        .patch<ActualizarNotificacionResponseDTO>(
          `${environment.apiUrlV2}/workspace/notificaciones/leer-todas`,
          {},
          { headers: this.construirHeaders() }
        )
        .toPromise();
      if (response.error) throw new Error(String(response.respuesta));
      return Number(response.respuesta?.actualizadas) || 0;
    } catch (error) {
      throw new Error(
        this.obtenerMensajeError(error, 'No se pudieron marcar las notificaciones')
      );
    }
  }

  /**
   * Archiva solamente la entrega del usuario autenticado; la cabecera
   * compartida y las bandejas de otros usuarios permanecen intactas.
   */
  async archivar(idNotificacion: number): Promise<void> {
    try {
      const response = await this.http
        .patch<ActualizarNotificacionResponseDTO>(
          `${environment.apiUrlV2}/workspace/notificaciones/${idNotificacion}/archivar`,
          {},
          { headers: this.construirHeaders() }
        )
        .toPromise();
      if (response.error) throw new Error(String(response.respuesta));
    } catch (error) {
      throw new Error(
        this.obtenerMensajeError(error, 'No se pudo archivar la notificación')
      );
    }
  }

  /**
   * Abre una conexión autenticada, registra su estado en la consola del navegador
   * y publica invalidaciones de notificaciones y de publicaciones de plataforma
   * (evento global); el contenido confiable siempre se recarga del API.
   */
  conectar(): void {
    this.desconectar();
    const token = this.requestServ.master?.apiKey;
    if (!token) return;

    this.socket = io(this.obtenerOrigenSocket(), {
      auth: { token },
      transports: ['websocket', 'polling'],
      reconnection: true,
    });
    this.socket.on(
      'notificaciones:cambio',
      (cambio: CambioNotificacionesSocketDTO) => this.cambiosSubject.next(cambio)
    );
    this.socket.on(
      'publicaciones:cambio',
      (cambio: CambioPublicacionesSocketDTO) => this.publicacionesSubject.next(cambio)
    );
    this.socket.on('connect', () => {
      console.info('[Notificaciones] WebSocket conectado.');
    });
    this.socket.on('disconnect', (motivo: string) => {
      console.info(`[Notificaciones] WebSocket desconectado por transporte: ${motivo}.`);
    });
    this.socket.io.on('reconnect', () => this.reconexionSubject.next());
  }

  /**
   * Cierra el transporte antes de liberar listeners para registrar la desconexión
   * real y evitar eventos cruzados entre sesiones o empresas distintas.
   */
  desconectar(): void {
    if (!this.socket) return;
    this.socket.disconnect();
    this.socket.removeAllListeners();
    this.socket.io.removeAllListeners();
    this.socket = null;
  }
}
